import { MAX_LOOP_COUNT } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import React, { FC, useRef } from 'react'
import { Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { styled } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import { Box, Row } from '../../core/Components'
import { ChevronLeft, ChevronRight, PlusIcon } from '../../core/icons'
import { gray8 } from '../../platform/color'
import { LOOP_LETTERS } from './InstrumentLoop'
import { MidiVisualizer } from './LoopNoteVisualizer'

const useLoopDropdown = ({
  stemIndex,
  section,
  closeDropdown
}: {
  stemIndex: number
  section: number
  closeDropdown: () => void
}) => {
  const { studio } = useTreeState()
  const stem = studio.song.stems[stemIndex]

  const handleAddExistingLoop = (index: number) => {
    studio.disableAudioUpdates()
    studio.song.updateLoopForStem(stemIndex, section, index + 1)
    studio.enableAudioUpdates()

    closeDropdown()
  }

  const handleAddNewLoop = (index: number) => {
    studio.disableAudioUpdates()
    studio.song.addLoopToStem(stemIndex)

    handleAddExistingLoop(index)
  }

  return { stem, handleAddExistingLoop, handleAddNewLoop }
}

const StyledLoopContainer = styled(Row, {
  backgroundColor: 'white',
  borderRadius: '$10',
  padding: '16px 30px',
  paddingBottom: '$40',
  boxShadow: '$big',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '$10',
  position: 'relative',
  width: '400px'
})

const StyledAddLoop = styled('div', {
  borderRadius: '$10',
  cursor: 'pointer'
})

const StyledAddNewLoop = styled(StyledAddLoop, {
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '$gray04',
  justifyContent: 'center',
  height: '100%',
  transition: '0.15s ease background-color',
  minHeight: '80px',
  '&:hover': {
    backgroundColor: '$gray05'
  }
})

const StyledAddExistingLoop = styled(StyledAddLoop, {
  padding: '$8',
  paddingTop: '$4',
  color: '$gray07'
})

const StyledButtonContainer = styled(Box, {
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '50%',
  backgroundColor: '$gray03',
  width: '24px',
  maxWidth: '24px',
  height: '24px',
  transition: '100ms',
  zIndex: 10,
  '&:hover': {
    backgroundColor: '$gray04'
  }
})

interface Props {
  stemIndex: number
  section: number
  close: () => void
  loopColor: string
}

const SelectLoopDropdown: FC<Props> = ({ stemIndex, section, close, loopColor }) => {
  const [isHovering, setIsHovering] = React.useState(null)
  const { stem, handleAddNewLoop, handleAddExistingLoop } = useLoopDropdown({
    stemIndex,
    section,
    closeDropdown: close
  })

  const prevRef = useRef(null)
  const nextRef = useRef(null)

  const loops = stem.loops
  const loopCount = loops.length

  return (
    <StyledLoopContainer css={loopCount < 4 ? { width: loopCount === 0 ? '220px' : '400px' } : {}}>
      <div style={{ width: '100%' }}>
        {loopCount > 0 ? (
          <Swiper
            slidesPerView={2}
            onInit={(swiper) => {
              // @ts-ignore
              swiper.params.navigation.prevEl = prevRef.current
              // @ts-ignore
              swiper.params.navigation.nextEl = nextRef.current
              swiper.navigation.init()
              swiper.navigation.update()
            }}
            modules={[Navigation]}
          >
            {!stem.isRemixed && stem.loops.length < MAX_LOOP_COUNT && (
              <SwiperSlide>
                <Box css={{ padding: '$10' }}>
                  <AddNewLoop onClick={() => handleAddNewLoop(loopCount)} />
                </Box>
              </SwiperSlide>
            )}
            {Array.from(Array(loopCount).keys()).map((_, index) => (
              <SwiperSlide key={index}>
                <Box css={{ padding: '$10' }}>
                  <StyledAddExistingLoop
                    css={{ backgroundColor: loopColor }}
                    onClick={() => handleAddExistingLoop(index)}
                    onMouseEnter={() => setIsHovering(index)}
                    onMouseLeave={() => setIsHovering(null)}
                  >
                    {LOOP_LETTERS[index]}
                    <MidiVisualizer
                      stem={stem}
                      loopNumber={index}
                      style={{ height: '50px' }}
                      fill={gray8}
                    />
                  </StyledAddExistingLoop>
                </Box>
              </SwiperSlide>
            ))}
            <Row justifySpaceBetween>
              <StyledButtonContainer ref={prevRef}>
                <ChevronLeft />
              </StyledButtonContainer>
              <StyledButtonContainer ref={nextRef}>
                <ChevronRight />
              </StyledButtonContainer>
            </Row>
          </Swiper>
        ) : (
          <AddNewLoop onClick={() => handleAddNewLoop(loopCount)} />
        )}
      </div>
    </StyledLoopContainer>
  )
}

const StyledPlusIcon = styled(PlusIcon, {
  height: '30px',
  width: '30px',
  color: '$gray10'
})

interface AddLoopProps {
  onClick: () => void
}

const AddNewLoop: React.FC<AddLoopProps> = ({ onClick }) => (
  <StyledAddNewLoop onClick={onClick}>
    <StyledPlusIcon />
  </StyledAddNewLoop>
)

export default observer(SelectLoopDropdown)
